/**
 * StorageManager.ts — SUPABASE FOUNDATION™ orchestration layer.
 *
 * Responsibilities (per sprint brief): provider selection, fallback
 * management, synchronization, conflict handling, diagnostics.
 *
 * Architecture contract:
 *   • UI components NEVER import this — Domain Services do.
 *   • Primary provider: SupabaseStorageProvider (api-server → Supabase;
 *     service-role key never reaches the browser).
 *   • Secondary: LocalCacheProvider + each Domain Service's own localStorage
 *     key (the synchronous cache/offline layer — unchanged).
 *   • Offline: writes queue in localStorage, replayed on 'online' event and
 *     on a 30s interval. Never lose data, never throw, never block a local write.
 *   • Conflict model: last-writer-wins on ISO updatedAt; hydrate() only hands
 *     back a cloud payload strictly newer than the last local write.
 *   • Event Bus: publishes DataSynchronized / PersistenceFailed /
 *     SystemStateChanged through an injected publisher (attached in
 *     core/bootstrap.ts — avoids an import cycle with Roamer Core).
 */

import type { CloudDomain, ProviderHealth } from './StorageProvider';
import { SupabaseStorageProvider } from './SupabaseStorageProvider';
import { LocalCacheProvider } from './LocalCacheProvider';

export type { CloudDomain } from './StorageProvider';

const QUEUE_KEY = 'nomad:cloud-sync-queue:v1';
const META_KEY = 'nomad:cloud-sync-meta:v1';
export const CLOUD_SYNC_EVENT = 'nomad:cloud-sync';
const FLUSH_INTERVAL_MS = 30_000;
const MAX_QUEUE = 200;

export type StorageBusEventName = 'SystemStateChanged' | 'PersistenceFailed' | 'DataSynchronized';
export type StorageEventPublisher = (name: StorageBusEventName, payload: Record<string, unknown>) => void;

interface QueuedWrite {
  domain: CloudDomain;
  key: string;
  payload: unknown;
  updatedAt: string; // ISO
  /** Tombstone: this is a delete, not a write. Queued + replayed like writes. */
  deleted?: boolean;
}

interface SyncMeta {
  /** `${domain}/${key}` → ISO timestamp of last local write. */
  lastLocalWrite: Record<string, string>;
  /** `${domain}/${key}` → ISO timestamp last confirmed synced to cloud. */
  lastSynced: Record<string, string>;
}

export interface StorageDiagnostics {
  provider: string;
  cloud: ProviderHealth;
  local: ProviderHealth;
  queuedWrites: number;
  retryCount: number;
  lastSynced: Record<string, string>;
  lastSyncAt: string | null;
  syncHealth: 'healthy' | 'degraded' | 'offline';
}

function hasStorage(): boolean {
  try { return typeof localStorage !== 'undefined'; } catch { return false; }
}

function readJSON<T>(key: string, fallback: T): T {
  if (!hasStorage()) return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch { return fallback; }
}

function writeJSON(key: string, value: unknown): void {
  if (!hasStorage()) return;
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* best-effort */ }
}

function emptyMeta(): SyncMeta {
  return { lastLocalWrite: {}, lastSynced: {} };
}

export class StorageManager {
  private readonly cloud = new SupabaseStorageProvider();
  private readonly local = new LocalCacheProvider();
  private publisher: StorageEventPublisher | null = null;
  private flushing = false;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => { void this.flush(); });
      setInterval(() => { void this.flush(); }, FLUSH_INTERVAL_MS);
    }
  }

  /** Wired from core/bootstrap.ts so sync events flow through the Core bus. */
  attachEventPublisher(publisher: StorageEventPublisher): void {
    this.publisher = publisher;
  }

  /**
   * Write-through push. The Domain Service has ALREADY written its own
   * localStorage cache; this records the write timestamp, mirrors into the
   * local doc-store fallback, and pushes to the cloud (queueing on failure).
   * Never throws, never blocks.
   */
  push(domain: CloudDomain, key: string, payload: unknown): void {
    const updatedAt = new Date().toISOString();
    // ── BOOT_PROBE: Q2 — cloud write (push) path ──────────────────────────────
    const cloudHealth = this.cloud.getHealth();
    console.log(
      `[BOOT_PROBE] StorageManager.push: domain=${domain} key=${key} ` +
      `cloudReachable=${cloudHealth.reachable} updatedAt=${updatedAt}`,
    );
    const meta = readJSON<SyncMeta>(META_KEY, emptyMeta());
    meta.lastLocalWrite[`${domain}/${key}`] = updatedAt;
    writeJSON(META_KEY, meta);
    void this.local.write(domain, key, payload, updatedAt);
    void this.send({ domain, key, payload, updatedAt });
  }

  /**
   * Delete the cloud + fallback copies. The delete is recorded as a local
   * write (tombstone timestamp) so hydrate() can never resurrect an older
   * cloud copy, and failed cloud deletes queue for replay like writes.
   */
  remove(domain: CloudDomain, key: string): void {
    const updatedAt = new Date().toISOString();
    const meta = readJSON<SyncMeta>(META_KEY, emptyMeta());
    meta.lastLocalWrite[`${domain}/${key}`] = updatedAt;
    writeJSON(META_KEY, meta);
    void this.local.delete(domain, key);
    void this.send({ domain, key, payload: null, updatedAt, deleted: true });
  }

  /**
   * Boot hydration: returns the cloud payload ONLY if strictly newer than the
   * last local write for this document; otherwise null (local cache stands).
   */
  async hydrate<T>(domain: CloudDomain, key: string): Promise<T | null> {
    // ── BOOT_PROBE: Q3 — cloud read (hydrate) path ────────────────────────────
    console.log(`[BOOT_PROBE] StorageManager.hydrate: domain=${domain} key=${key}`);
    try {
      const doc = await this.cloud.read<T>(domain, key);
      if (!doc) {
        console.log(`[BOOT_PROBE] StorageManager.hydrate: cloud returned null for ${domain}/${key} → local cache stands`);
        return null;
      }
      const meta = readJSON<SyncMeta>(META_KEY, emptyMeta());
      const localTs = meta.lastLocalWrite[`${domain}/${key}`];
      const cloudTs = doc.updatedAt;
      const isNewer = !localTs || Date.parse(localTs) < Date.parse(cloudTs);
      console.log(
        `[BOOT_PROBE] StorageManager.hydrate ${domain}/${key}: ` +
        `cloudUpdatedAt=${cloudTs} localUpdatedAt=${localTs ?? 'never'} ` +
        `cloudIsNewer=${isNewer} → ${isNewer ? 'ADOPTING cloud record' : 'SKIPPING (local is same age or newer)'}`,
      );
      if (localTs && Date.parse(localTs) >= Date.parse(doc.updatedAt)) return null;
      meta.lastSynced[`${domain}/${key}`] = doc.updatedAt;
      meta.lastLocalWrite[`${domain}/${key}`] = doc.updatedAt;
      writeJSON(META_KEY, meta);
      void this.local.write(domain, key, doc.payload, doc.updatedAt);
      this.publish('SystemStateChanged', {
        reason: 'cloud-hydrated',
        legacyEvent: CLOUD_SYNC_EVENT,
        domain,
      });
      return doc.payload;
    } catch (err) {
      console.log(`[BOOT_PROBE] StorageManager.hydrate ${domain}/${key}: THREW → ${String(err)}`);
      return null; // offline / unconfigured — local cache stands
    }
  }

  /** Reachability probe (Dev Center + integrity audit). */
  async checkCloudHealth(): Promise<ProviderHealth> {
    return this.cloud.checkHealth();
  }

  getDiagnostics(): StorageDiagnostics {
    const queue = readJSON<QueuedWrite[]>(QUEUE_KEY, []);
    const meta = readJSON<SyncMeta>(META_KEY, emptyMeta());
    const cloud = this.cloud.getHealth();
    const syncTimes = Object.values(meta.lastSynced).sort();
    const lastSyncAt = syncTimes.length > 0 ? syncTimes[syncTimes.length - 1] : null;
    const syncHealth: StorageDiagnostics['syncHealth'] = cloud.reachable
      ? (queue.length === 0 ? 'healthy' : 'degraded')
      : 'offline';
    return {
      provider: this.cloud.id,
      cloud,
      local: this.local.getHealth(),
      queuedWrites: queue.length,
      retryCount: this.cloud.getRetryCount(),
      lastSynced: { ...meta.lastSynced },
      lastSyncAt,
      syncHealth,
    };
  }

  /** Replay queued writes now (also runs on 'online' + interval). */
  async flush(): Promise<void> {
    if (this.flushing) return;
    const queue = readJSON<QueuedWrite[]>(QUEUE_KEY, []);
    if (queue.length === 0) return;
    this.flushing = true;
    try {
      writeJSON(QUEUE_KEY, []); // drain; failures re-enqueue via send()
      for (const write of queue) await this.send(write);
    } finally {
      this.flushing = false;
    }
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  private async send(write: QueuedWrite): Promise<void> {
    try {
      if (write.deleted) {
        await this.cloud.delete(write.domain, write.key);
      } else {
        await this.cloud.write(write.domain, write.key, write.payload, write.updatedAt);
      }
      const meta = readJSON<SyncMeta>(META_KEY, emptyMeta());
      meta.lastSynced[`${write.domain}/${write.key}`] = write.updatedAt;
      writeJSON(META_KEY, meta);
      this.emitWindow(write.domain, write.key, 'synced');
      this.publish('DataSynchronized', {
        domain: write.domain,
        key: write.key,
        latencyMs: this.cloud.getHealth().lastLatencyMs,
        deleted: write.deleted === true,
      });
    } catch (err) {
      this.enqueue(write);
      this.emitWindow(write.domain, write.key, 'queued');
      this.publish('PersistenceFailed', {
        domain: write.domain,
        key: write.key,
        reason: err instanceof Error ? err.message : 'unknown',
        queued: true,
      });
    }
  }

  private enqueue(write: QueuedWrite): void {
    const queue = readJSON<QueuedWrite[]>(QUEUE_KEY, []);
    // Coalesce: keep only the newest write per document.
    const filtered = queue.filter((q) => !(q.domain === write.domain && q.key === write.key));
    filtered.push(write);
    writeJSON(QUEUE_KEY, filtered.slice(-MAX_QUEUE));
  }

  private publish(name: StorageBusEventName, payload: Record<string, unknown>): void {
    try { this.publisher?.(name, payload); } catch { /* bus problems never break persistence */ }
  }

  private emitWindow(domain: string, key: string, status: 'synced' | 'queued'): void {
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(new CustomEvent(CLOUD_SYNC_EVENT, { detail: { domain, key, status } }));
    } catch { /* ignore */ }
  }
}

/** App-wide singleton — Domain Services import this; UI never does. */
export const storageManager = new StorageManager();
